import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import { useDispatch } from "react-redux";
import API from "helpers/api";
import { isSunsetOverdue } from "helpers/versionPolicy";
import { showError, showSuccess } from "../../redux/common/actions";

const EMPTY = {
	platform: "ios",
	appIdentifier: "com.equipmentmanagement.emi3mobile",
	latestBuild: 0,
	latestVersion: "",
	minSupportedBuild: 0,
	sunsetDate: "",
	enforceUnsupported: false,
};

function MobileVersionControlSection() {
	const dispatch = useDispatch();
	const [form, setForm] = useState(EMPTY);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const res = await API.get("/api/app/version-policies");
			const policy = res?.data?.find((p) => p.platform === "ios");
			if (policy) {
				setForm({
					...policy,
					sunsetDate: policy.sunsetDate ? policy.sunsetDate.slice(0, 10) : "",
				});
			}
		} catch {
			dispatch(showError("Could not load the version policy."));
		} finally {
			setLoading(false);
		}
	}, [dispatch]);

	useEffect(() => {
		load();
	}, [load]);

	const setField = (key) => (e) =>
		setForm((prev) => ({ ...prev, [key]: e.target.value }));

	const handleSave = async () => {
		setSaving(true);
		try {
			await API.put("/api/app/version-policy", {
				...form,
				latestBuild: Number(form.latestBuild),
				minSupportedBuild: Number(form.minSupportedBuild),
				sunsetDate: form.sunsetDate || null,
			});
			dispatch(showSuccess("Version policy saved."));
			load();
		} catch {
			dispatch(showError("Could not save the version policy."));
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	const overdue = isSunsetOverdue(form.sunsetDate);

	return (
		<Box sx={{ maxWidth: 640, display: "flex", flexDirection: "column", gap: 2.5 }}>
			<Box sx={{ display: "flex", gap: 2 }}>
				<TextField label="Platform" value={form.platform} disabled fullWidth />
				<TextField label="App Identifier" value={form.appIdentifier} disabled fullWidth />
			</Box>
			<Box sx={{ display: "flex", gap: 2 }}>
				<TextField label="Latest Version" value={form.latestVersion ?? ""} onChange={setField("latestVersion")} fullWidth />
				<TextField label="Latest Build" type="number" value={form.latestBuild} onChange={setField("latestBuild")} fullWidth />
			</Box>
			<TextField
				label="Min Supported Build"
				type="number"
				value={form.minSupportedBuild}
				onChange={setField("minSupportedBuild")}
				helperText="Builds below this are unsupported; builds at or above it but behind the latest get update prompts."
				fullWidth
			/>
			<TextField
				label="Sunset Date"
				type="date"
				value={form.sunsetDate}
				onChange={setField("sunsetDate")}
				InputLabelProps={{ shrink: true }}
				error={overdue}
				helperText={overdue ? "This sunset date has passed — raise the support floor to enforce it." : "Optional. Supported-band builds show an expiry warning with this date."}
				fullWidth
			/>
			<FormControlLabel
				control={
					<Switch
						checked={Boolean(form.enforceUnsupported)}
						onChange={(e) => setForm((prev) => ({ ...prev, enforceUnsupported: e.target.checked }))}
					/>
				}
				label="Enforce hard block for unsupported builds"
			/>
			<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
				<Button onClick={handleSave} disabled={saving} variant="contained" sx={{ backgroundColor: "#23BB79" }}>
					{saving ? "Saving…" : "Save policy"}
				</Button>
			</Box>
		</Box>
	);
}

export default MobileVersionControlSection;
